import { ThreeDCardPreview } from '@sendoutcards/editor'
import {
  Drawer,
  DrawerProps,
  Flex,
  Text,
} from '@sendoutcards/quantum-design-ui'
import { useRef } from 'react'
import { MinimalDigitalCardFragment } from 'src/graphql/generated/graphql'
import {
  mapPanelControlNames,
  mapPanelImages,
} from 'src/helpers/threeDCard/mapPanelImages'
import { useActions, useEffect, useSelector, useState } from 'src/hooks'
import { DuvetV2 } from '../DuvetV2/DuvetV2'
import useSnapPoints from 'src/hooks/useSnapPoints'
import { RecipientView } from '../RecipientView/RecipientView'
import { DuvetHeader } from '../DuvetHeader/DuvetHeader'
import { Icon } from 'src/design_system/atoms/icons/components/Icon'
import ActRequestCard from '../ActRequestCard/ActRequestCard'
import { ActUpsaleDialog } from '../FreeCardSendModal/ActUpsaleDialog'
import { useGetActRecipientActionScreenContent } from 'src/react_query'
import { ThrowOutDemoCatalog } from '../ThrowOutDemoCatalog'
type ViewActProps = {
  digitalCard: MinimalDigitalCardFragment
  isDemo?: boolean
}

const ViewAct: React.FC<ViewActProps> = props => {
  const { digitalCard, isDemo } = props
  const { data: recipientActionScreenContent } =
    useGetActRecipientActionScreenContent()
  const freeCardUpsaleContent =
    recipientActionScreenContent?.freeCardUpsaleContent
  const freeCardUpsaleMarketingImageUrl =
    recipientActionScreenContent?.freeCardUpsaleMarketingImageUrl

  const leftDrawerRef = useRef<HTMLDivElement>(null)
  const isMobile = useSelector(state => state.window.width < 500)
  const isDesktop = useSelector(state => state.window.width >= 961)
  const controlContainerRef = useRef<HTMLDivElement>(null)
  const [shouldPortalControls, setShouldPortalControls] = useState(false)
  const [shouldShowRequestCard, setShouldShowRequestCard] = useState(false)
  const [shouldShowFreeCardUpsale, setShouldShowFreeCardUpsale] =
    useState(false)
  const [shouldShowDemoCatalog, setShouldShowDemoCatalog] = useState(false)

  const controlPanelNames = mapPanelControlNames(digitalCard.card.panels)

  const actions = useActions()

  const { duvetState, isRecipientDrawerOpen } = useSelector(state => state.act)

  const { snapPoints, openValue, handleSnap } = useSnapPoints(
    'recipientView',
    duvetState,
  )

  const dummyRecipient = {
    title: digitalCard.shareMessageTitle,
    description: digitalCard.shareMessageBody,
    recipient: {
      firstname: digitalCard.sponsor.firstName ?? '',
      lastname: digitalCard?.sponsor.lastName ?? '',
      imageUrl: digitalCard?.sponsor.storefront?.mugshot?.url,
    },
    primaryAction: {
      title: 'Request Printed Copy',
      onClick: () => setShouldShowRequestCard(true),
    },
    secondaryAction: {
      title: 'Send Your Own',
      onClick: () => setShouldShowFreeCardUpsale(true),
    },
  }

  const renderDrawerContent = <RecipientView {...dummyRecipient} />

  useEffect(() => {
    if (controlContainerRef.current) {
      setShouldPortalControls(true)
    }
  }, [])

  const containerStyles = {
    height: 'calc(100dvh - 65px)',
    width: '100dvw',
    position: 'fixed',
    top: 0,
    left: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    backgroundColor: '#FEFEFE',
  } as const

  const drawerProps: DrawerProps = {
    isTabHidden: false,
    zIndex: 10,
    location: 'left',
    offset: 0,
    openValue: 350,
    isOpen: isRecipientDrawerOpen,
    onClick: () => actions.setRecipientDrawerOpen(!isRecipientDrawerOpen),
    overflow: 'hidden',
    tabMode: 'dark',
  }

  return (
    <div style={{ ...containerStyles }}>
      {isDesktop && (
        <Drawer {...drawerProps}>
          <div style={{ height: '100%' }} ref={leftDrawerRef}>
            <Flex
              outset={{ vertical: 'x4' }}
              justifyContent="center"
              flexDirection="column"
              alignItems="center"
            >
              <Icon name="promptings_logo_full_gradient" size={200} />
              <Flex flexDirection="row" columnGap="x_25">
                <Text
                  content="Powered by"
                  type="body"
                  isItalic
                  lineHeight={1.5}
                  style={{ fontSize: '11px', color: '#6B7280' }}
                />
                <Icon name="soc_logo" size={120} color="#000" />
              </Flex>
            </Flex>
            <Flex
              width="100%"
              height="100%"
              alignItems="center"
              flexDirection="column"
              inset="x1"
            >
              {renderDrawerContent}
            </Flex>
          </div>
        </Drawer>
      )}

      {shouldPortalControls && (
        <Flex
          outset={{ top: 'x4' }}
          height="100%"
          justifyContent="center"
          alignItems="center"
        >
          <ThreeDCardPreview
            cardType={digitalCard.card.type}
            orientation={
              digitalCard.card.isHorizontal ? 'LANDSCAPE' : 'PORTRAIT'
            }
            panels={mapPanelImages(
              digitalCard.card.frontPreviewUrl,
              digitalCard.card.insidePreviewUrl,
              digitalCard.card.backPreviewUrl,
              digitalCard.card.flapPreviewUrl,
              !!digitalCard.card.flapPreviewUrl,
              digitalCard.card.isHorizontal,
            )}
            height="calc(100dvh - 350px)"
            control={{
              type: 'Portal',
              portalId: 'controls3DCard',
              panelNames: controlPanelNames,
              shouldOnlyShowDropdown: isMobile,
            }}
            bgGradientColor={{ shouldDisplayBg: true }}
          />
        </Flex>
      )}
      <div
        style={{
          marginTop: 'auto',
          marginBottom: '1rem',
          width: '100%',
          padding: '0 1rem',
          zIndex: 8,
          position: 'relative',
        }}
      >
        <div
          ref={controlContainerRef}
          id="controls3DCard"
          style={{ margin: '0 auto 2.5rem', maxWidth: '500px' }}
        />
      </div>
      {!isDesktop && (
        <DuvetV2
          snapPoints={snapPoints}
          onSnap={handleSnap}
          onClose={() => {
            actions.setDuvetState('navigation')
          }}
          openValue={openValue}
          isOpen={true}
          shouldShowCloseButton={true}
        >
          <DuvetHeader
            shouldShowMoreDetails={!!(duvetState !== 'navigation')}
            onShowDetails={() =>
              !!(duvetState !== 'navigation')
                ? actions.setDuvetState('navigation')
                : actions.setDuvetState('previewCollapsed')
            }
          />
          {renderDrawerContent}
        </DuvetV2>
      )}
      {shouldShowRequestCard &&
        digitalCard.recipients.length > 0 &&
        digitalCard.recipients[0].contactRequest && (
          <ActRequestCard
            digitalCardToken={digitalCard.recipients[0].token}
            contactRequestToken={digitalCard.recipients[0].contactRequest.token}
            cardImgUrl={digitalCard.card.frontPreviewUrl}
            onBack={() => {
              setShouldShowRequestCard(false)
            }}
            isDemo={isDemo}
          />
        )}
      {shouldShowFreeCardUpsale && digitalCard && (
        <ActUpsaleDialog
          isOpen={shouldShowFreeCardUpsale}
          title={freeCardUpsaleContent?.title ?? 'marketing Paragraph title'}
          description={
            freeCardUpsaleContent?.content ?? 'marketing paragraph description'
          }
          onClose={() => setShouldShowFreeCardUpsale(false)}
          primaryAction={{
            title: 'Send',
            onClick: () => {
              setShouldShowFreeCardUpsale(false)
              if (isDemo) {
                setShouldShowDemoCatalog(true)
              } else {
                alert('free-click')
              }
            },
          }}
          socMediaImage={{
            filename: freeCardUpsaleMarketingImageUrl?.url ?? 'freecardhero',
          }}
        />
      )}
      {shouldShowDemoCatalog && (
        <ThrowOutDemoCatalog onClose={() => setShouldShowDemoCatalog(false)} />
      )}
    </div>
  )
}

export default ViewAct
